import React, { useEffect, useState } from 'react';
import { Icon, IconName } from '../../primitives/Icon';
import Button from '../../primitives/Button';
import BaseDrawer from '../Drawer/BaseDrawer';
import './MenuDrawer.css';
import LogoSVG from '../../primitives/LogoSVG';
import { useWouterNavigate } from '../../../../hooks/useWouterNavigation';
import { useAuth } from '../../../../contexts/AuthContext';
import { usePWAInstallStore } from '../../../../stores/pwaInstallStore';

interface MenuDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  isOrganizer?: boolean;
  onOrganizerClick?: () => void;
  onContactClick?: () => void;
  onIdeaClick?: () => void;
  onAmbassadorClick?: () => void;
}

interface MenuItem {
  icon: IconName;
  label: string;
  onClick?: () => void;
  highlight?: boolean;
}

const MenuDrawer: React.FC<MenuDrawerProps> = ({
  isOpen,
  onClose
}) => {
  const [showContactModal, setShowContactModal] = useState(false);
  const [showAmbassadorModal, setShowAmbassadorModal] = useState(false);
  const [isNavigating] = useState(false);
  const [navigationError, setNavigationError] = useState<string | null>(null);
  const navigate = useWouterNavigate();
  const { user } = useAuth();
  const { deferredPrompt, isInstalled, triggerInstall } = usePWAInstallStore();

  useEffect(() => {
    const handleOnlineStatus = () => {
      if (navigator.onLine) {
        setNavigationError(null);
      }
    };

    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', () => {
      setNavigationError("You are offline. Some features may be limited.");
    });

    return () => {
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', () => {});
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      const success = await triggerInstall();
      if (success) {
        onClose();
      }
    } else if (!isInstalled) {
      // Fallback: manual instructions for browsers without prompt support
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isAndroid = /Android/.test(navigator.userAgent);

      if (isIOS) {
        alert('Add to Home Screen:\n1. Tap the Share button\n2. Select "Add to Home Screen"');
      } else if (isAndroid) {
        alert('Install the app:\nTap the menu and select "Install app" or "Add to Home Screen"');
      } else {
        alert('Add to Home Screen:\nLook for "Install" or "Add to Home Screen" in your browser menu');
      }
    }
  };

  const menuItems: MenuItem[] = [
    {
      icon: 'Megaphone',
      label: 'Post an Event',
      onClick: () => {
        // Fermer d'abord le drawer
        onClose();

        // Utiliser un timeout pour s'assurer que le drawer est fermé
        setTimeout(() => {
          if (user?.uid) {
            navigate('/organizer');
          } else {
            sessionStorage.setItem('redirectAfterLogin', '/organizer');
            navigate('/login');
          }
        }, 100);
      }
    },
    {
      icon: 'Users',
      label: 'Ambassador Program',
      onClick: () => setShowAmbassadorModal(true)
    },
    {
      icon: 'Instagram',
      label: 'Follow us on Instagram',
      onClick: () => window.open('https://www.instagram.com/what2do.today/', '_blank')
    },
    {
      icon: 'MessageSquare',
      label: 'Contact & Feedback',
      onClick: () => setShowContactModal(true)
    },
    {
      icon: 'Info',
      label: 'About',
      onClick: () => {
        onClose();
        setTimeout(() => {
          navigate('/about');
        }, 100);
      }
    },
    {
      icon: 'Shield',
      label: 'Legal & Privacy Policy',
      onClick: () => {
        onClose();
        setTimeout(() => {
          navigate('/legal');
        }, 100);
      }
    },
    // Only show install option if not already installed
    ...(!isInstalled ? [{
      icon: 'Download' as IconName,
      label: 'Get the App',
      onClick: handleInstallClick,
      highlight: true
    }] : [])
  ];

  const handleCloseModal = () => {
    setShowContactModal(false);
    if (window.history.state === null) {
      window.history.back();
    }
  };

  const handleCloseAmbassadorModal = () => {
    setShowAmbassadorModal(false);
    if (window.history.state === null) {
      window.history.back();
    }
  };

  useEffect(() => {
    if (showContactModal) {
      window.history.pushState('modal', '', window.location.pathname);

      const handlePopState = () => {
        setShowContactModal(false);
      };

      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [showContactModal]);

  useEffect(() => {
    if (showAmbassadorModal) {
      window.history.pushState('modal', '', window.location.pathname);

      const handlePopState = () => {
        setShowAmbassadorModal(false);
      };

      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [showAmbassadorModal]);

 

  return (
    <>
      <BaseDrawer
        isOpen={isOpen}
        onClose={onClose}
        className="menu-drawer"
      >
        <div className="menu-drawer-header">
          <h2>Menu</h2>
        </div>
        <div className="menu-drawer-content">
          {navigationError && (
            <div className="menu-drawer-error">
              {navigationError}
            </div>
          )}
          {menuItems.map((item, index) => (
            <React.Fragment key={index}>
              {/* Divider after Ambassador Program (before Instagram) */}
              {index === 2 && <div className="menu-drawer-divider" />}
              {/* Divider after Contact & Feedback (before About / Legal) */}
              {index === 4 && <div className="menu-drawer-divider" />}
              <button
                className={`menu-item ${item.highlight ? 'menu-item--highlight' : ''} ${
                  isNavigating ? 'menu-item--loading' : ''
                }`}
                onClick={item.onClick}
                disabled={isNavigating}
                aria-label={item.label}
              >
                <Icon name={item.icon} size={24} />
                <span>{item.label}</span>
                {isNavigating && <span className="menu-item__spinner" />}
              </button>
            </React.Fragment>
          ))}
        </div>
      </BaseDrawer>

      {showContactModal && (
        <div 
          className="menu-drawer-modal" 
          onClick={handleCloseModal}
        >
          <div className="menu-drawer-modal__content" onClick={e => e.stopPropagation()}>
            <h3 className="menu-drawer-modal__title">
              Get in touch! <LogoSVG variant="singeOrange" size="lg"  />
            </h3>
            <div className="menu-drawer-modal__buttons">
              <Button
                variant="primary"
                onClick={() => window.location.href = "mailto:nico@what2do.today"}
              >
                <Icon name="Mail" size={20} />
                Send us an email
              </Button>
              <Button
                variant="secondary"
                onClick={() => window.open("https://forms.gle/WsLPhinC1fS5eYJcA", "_blank")}
              >
                <Icon name="MessageSquare" size={20} />
                Share your feedback
              </Button>
            </div>
          </div>
        </div>
      )}

      {showAmbassadorModal && (
        <div
          className="menu-drawer-modal"
          onClick={handleCloseAmbassadorModal}
        >
          <div className="menu-drawer-modal__content" onClick={e => e.stopPropagation()}>
            <h3 className="menu-drawer-modal__title">
              Ambassador Program <LogoSVG variant="singeOrange" size="lg" />
            </h3>
            <p className="menu-drawer-modal__description">
              This program is currently under construction. Contact us by email to learn more about becoming an ambassador!
            </p>
            <div className="menu-drawer-modal__buttons">
              <Button
                variant="primary"
                onClick={() => window.location.href = "mailto:nico@what2do.today?subject=Ambassador Program Inquiry"}
              >
                <Icon name="Mail" size={20} />
                Contact us
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MenuDrawer; 